import type { DesignModuleSpecJson } from '../data/contracts';
import type { TemplateNode } from '@/uptk/core/components/moduleEntity/template/templateTypes';

/** E2E-FIX1 (K, DIAG-E2E-FIN cause 5) : une section native ajoutée par le Maker dans une version antérieure (absente du prototype,
 * donc de toute capture) se perd quand une page repart de sa capture ou quand une version l'a perdue. Le plan la liste comme geste en
 * attente : « Reprendre la section … de la vN ». Une section perdue = un élément de la page de la version antérieure qui porte au moins
 * deux liaisons et un titre (h1-h4) qu'aucun texte de la page courante ne porte. Le geste recopie la section au même endroit (même
 * conteneur), avec ses textes, comme décision du Maker. Générique : aucune page ni section nommées. */
export type LostSection = { page: string; version: number; path: (string | number)[]; heading: string; bindings: number };
type Json = Record<string, unknown>;
type Page = { key: string; screen?: { blocks?: { template?: { root: TemplateNode } }[] } };
type Messages = Record<string, Record<string, string>>;
const BINDINGS = new Set(['field', 'aggregate', 'repeat', 'ratio', 'calculation']);
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4']);
const messagesOf = (spec: DesignModuleSpecJson): Messages => ((spec.presentation ?? {}) as { templateMessages?: Messages }).templateMessages ?? {};
const rootOf = (spec: DesignModuleSpecJson, page: string) => ((spec.pages ?? []) as Page[]).find(item => item.key === page)?.screen?.blocks?.find(block => block.template)?.template?.root;
const str = (value: unknown): string => typeof value === 'string' ? value : '';
const norm = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();
const textOf = (node: unknown, messages: Messages): string => {
  if (!node || typeof node !== 'object') return '';
  const value = node as Json;
  if (value.type === 'text') return str(value.token) || (messages[str(value.i18n)]?.fr ?? '');
  return Array.isArray(value.children) ? value.children.map(child => textOf(child, messages)).join(' ') : '';
};
const countBindings = (node: unknown): number => {
  if (!node || typeof node !== 'object') return 0;
  if (Array.isArray(node)) return node.reduce((sum, item) => sum + countBindings(item), 0);
  const value = node as Json;
  return (BINDINGS.has(str(value.type)) ? 1 : 0) + Object.entries(value).reduce((sum, [key, item]) => sum + (key === 'empty' ? 0 : countBindings(item)), 0);
};
const headingOf = (node: Json, messages: Messages): string => {
  const pending: unknown[] = [node];
  while (pending.length) {
    const item = pending.shift() as Json | undefined;
    if (!item || typeof item !== 'object') continue;
    if (item.type === 'el' && HEADINGS.has(str(item.tag))) { const text = textOf(item, messages).trim(); if (text) return text; }
    if (Array.isArray(item.children)) pending.push(...item.children);
  }
  return '';
};
const headings = (root: unknown, messages: Messages): Set<string> => {
  const out = new Set<string>();
  const walk = (node: unknown) => {
    if (!node || typeof node !== 'object') return;
    const value = node as Json;
    if (value.type === 'el' && HEADINGS.has(str(value.tag))) { const text = norm(textOf(value, messages)); if (text) out.add(text); }
    for (const [key, item] of Object.entries(value)) if (key !== 'empty' && item && typeof item === 'object') walk(item);
  };
  walk(root);
  return out;
};

export function lostSections(current: DesignModuleSpecJson, older: DesignModuleSpecJson, version: number): LostSection[] {
  const out: LostSection[] = [], before = messagesOf(older), now = messagesOf(current);
  for (const page of (older.pages ?? []) as Page[]) {
    const previous = rootOf(older, page.key), present = rootOf(current, page.key);
    if (!previous || !present) continue;
    const known = headings(present, now);
    const walk = (node: unknown, path: (string | number)[]) => {
      if (!node || typeof node !== 'object') return;
      const value = node as Json;
      if (value.type === 'el' && ['section', 'article', 'div', 'aside'].includes(str(value.tag))) {
        const heading = headingOf(value, before), bindings = countBindings(value);
        if (heading && bindings >= 2 && !known.has(norm(heading)) && headings(value, before).size === 1) { out.push({ page: page.key, version, path, heading, bindings }); return; }
      }
      if (Array.isArray(value.children)) value.children.forEach((child, index) => walk(child, [...path, 'children', index]));
    };
    walk(previous, []);
  }
  return out;
}

/** GAP-37 : whether a section heading comes from the prototype : it is in the captured root of the page (decision journal origins).
 *  A section whose heading no capture of the page carries was added by the Maker (« Reprendre des sections », Designer). */
export function headingFromCapture(spec: DesignModuleSpecJson, page: string, heading: string): boolean {
  const origins = (((spec.decisionJournal ?? {}) as { origins?: { page: string; root: unknown; messages?: Messages }[] }).origins ?? []).filter(origin => origin.page === page);
  return origins.some(origin => headings(origin.root, origin.messages ?? {}).has(norm(heading)));
}

/** Le geste « Reprendre la section » : au même endroit si le conteneur existe (sinon en fin de conteneur), textes compris. */
/** GAP-37 : `nearest` — when the container no longer exists (the page was put back to another template), the section goes at the end
 *  of the deepest existing container of its path, never dropped. */
export function restoreSections(current: DesignModuleSpecJson, older: DesignModuleSpecJson, sections: readonly LostSection[], options: { nearest?: boolean } = {}): DesignModuleSpecJson {
  const next = structuredClone(current), source = messagesOf(older), presentation = (next.presentation ?? {}) as { templateMessages?: Messages };
  const messages: Messages = { ...(presentation.templateMessages ?? {}) };
  for (const section of sections) {
    const node = section.path.reduce<unknown>((value, key) => value && typeof value === 'object' ? (value as Json)[key as string] : undefined, rootOf(older, section.page));
    const container = section.path.slice(0, -2), index = Number(section.path[section.path.length - 1]);
    const reach = (path: (string | number)[]) => path.reduce<unknown>((value, key) => value && typeof value === 'object' ? (value as Json)[key as string] : undefined, rootOf(next, section.page)) as Json | undefined;
    let parent = reach(container), at = index;
    if ((!parent || !Array.isArray(parent.children)) && options.nearest) {
      for (let length = container.length - 2; length >= 0 && (!parent || !Array.isArray(parent.children)); length -= 2) { parent = reach(container.slice(0, length)); at = Number.MAX_SAFE_INTEGER; }
    }
    if (!node || !parent || !Array.isArray(parent.children)) continue;
    const children = parent.children as unknown[];
    children.splice(Math.min(at, children.length), 0, structuredClone(node));
    // Every text key the section uses (texts, labels, composed messages) comes with it.
    for (const quoted of JSON.stringify(node).match(/"[^"\\]{3,200}"/g) ?? []) { const id = quoted.slice(1, -1); if (source[id] && !messages[id]) messages[id] = source[id]; }
  }
  next.presentation = { ...presentation, templateMessages: messages } as DesignModuleSpecJson['presentation'];
  return next;
}
